import type {ProfileFamilyResource, ProfileFamilyResourceCategoriesInner} from '@api';

type FamiliesAndCategories = {
    families: string[];
    categories: string[];
    categorySuggestion: string | null;
};

interface FamiliesCategories {
    preferredFamily(): string | null;

    allSelectedCategories(): string[];

    suggestedCategory(): string | null;

    hasCompleteSelection(): boolean;

    toRequest(): FamiliesAndCategories;
}

export class FamiliesCategoriesPredicted implements FamiliesCategories {
    private selectedFamilies: Set<string> = new Set();
    private selectedCategories: string[] = [];

    resetSelection(): void {
        this.selectedFamilies = new Set();
        this.selectedCategories = [];
    }

    selectCategory(family: string, category: string): void {
        this.selectedFamilies = family ? new Set(this.selectedFamilies.add(family)) : new Set();
        this.selectedCategories.push(category);
    }

    unSelectCategory(family: string, category: string, families: ProfileFamilyResource[]): void {
        if (family) {
            this.selectedCategories = this.selectedCategories.filter((cat) => cat !== category);

            if (this.selectedCategories.length === 0) {
                this.selectedFamilies = new Set();
            } else {
                let familyHasToBeDeleted = true;
                for (let i = 0; i < this.selectedCategories.length; i++) {
                    const savedCategory = this.selectedCategories[i];
                    const categoryStillReferenceFamilyToDelete = families
                        .filter((familyDef: ProfileFamilyResource) =>
                            familyDef.categories.map((cat: ProfileFamilyResourceCategoriesInner) => cat.categoryId).includes(savedCategory),
                        )
                        .map((familyDef: ProfileFamilyResource) => familyDef.familyId)
                        .includes(family);

                    if (categoryStillReferenceFamilyToDelete) {
                        familyHasToBeDeleted = false;
                    }
                }

                if (familyHasToBeDeleted) {
                    this.selectedFamilies.delete(family);
                }
            }
        } else {
            this.selectedFamilies = new Set();
        }
    }

    hasCompleteSelection(): boolean {
        return this.selectedFamilies.size > 0 && this.selectedCategories.length > 0;
    }

    allSelectedCategories(): string[] {
        return this.selectedCategories;
    }

    toRequest(): FamiliesAndCategories {
        return {
            families: [...this.selectedFamilies],
            categories: this.selectedCategories,
            categorySuggestion: null,
        };
    }

    suggestedCategory(): string | null {
        return null;
    }

    preferredFamily(): string | null {
        return this.selectedFamilies.values().next().value as string | null;
    }
}

export class FamilyCategoriesManual implements FamiliesCategories {
    private selectedFamily: string | null = null;
    private selectedCategories: string[] = [];
    private selectedOtherCategory: string | null = null;

    resetSelection(): void {
        this.selectedFamily = null;
        this.selectedCategories = [];
        this.selectedOtherCategory = null;
    }

    selectFamily(family: string): void {
        this.selectedFamily = family;
        this.selectedCategories = [];
        this.selectedOtherCategory = null;
    }

    selectCategories(categories: string[]): void {
        this.selectedCategories = categories;
    }

    selectOtherCategory(category: string): void {
        this.selectedOtherCategory = category;
    }

    hasCompleteSelection(): boolean {
        return (
            this.selectedFamily !== null &&
            (this.selectedCategories.length > 0 || (this.selectedOtherCategory !== null && this.selectedOtherCategory.trim() !== ''))
        );
    }

    allSelectedCategories(): string[] {
        return this.selectedCategories;
    }

    toRequest(): FamiliesAndCategories {
        return {
            families: this.selectedFamily ? [this.selectedFamily] : [],
            categories: this.selectedCategories,
            categorySuggestion: this.selectedOtherCategory,
        };
    }

    suggestedCategory(): string | null {
        return this.selectedOtherCategory;
    }

    preferredFamily(): string | null {
        return this.selectedFamily;
    }
}

export class FamiliesCategoriesEdition implements FamiliesCategories {
    predicted: FamiliesCategoriesPredicted = new FamiliesCategoriesPredicted();
    manual: FamilyCategoriesManual = new FamilyCategoriesManual();
    private familiesCategories: FamiliesCategories = this.predicted;

    switchToPrediction(): void {
        this.resetAll();
        this.familiesCategories = this.predicted;
    }

    switchToManual(): void {
        this.resetAll();
        this.familiesCategories = this.manual;
    }

    allSelectedCategories(): string[] {
        return this.familiesCategories.allSelectedCategories();
    }

    toRequest(): FamiliesAndCategories {
        return this.familiesCategories.toRequest();
    }

    hasCompleteSelection(): boolean {
        return this.familiesCategories.hasCompleteSelection();
    }

    suggestedCategory(): string | null {
        return this.familiesCategories.suggestedCategory();
    }

    preferredFamily(): string | null {
        return this.familiesCategories.preferredFamily();
    }

    resetAll(): void {
        this.predicted.resetSelection();
        this.manual.resetSelection();
    }
}
